import { createContext, useContext, useEffect, PropsWithChildren, useMemo } from 'react';
import { useGetTenantsQuery } from '@/services';
import { useAuth } from './auth-context';
import { skipToken } from '@reduxjs/toolkit/query';
import { RootState } from '@/app/store';
import { setCurrentTenant as setCurrentTenantAction, Tenant } from '@/ducks/auth-slice';
import { useAppDispatch, useAppSelector } from '@/ducks/useful-hooks';

interface TenantContextType {
  currentTenant: Tenant | null | undefined;
  tenants: Tenant[];
  isLoading: boolean;
  setCurrentTenant: (tenant: Tenant) => void;
  refreshTenants: () => void;
}

const TenantContext = createContext<TenantContextType | null>(null);

export function TenantProvider({ children }: PropsWithChildren) {
  const { isAuthenticated } = useAuth();
  const dispatch = useAppDispatch();
  const currentTenant = useAppSelector((state: RootState) => state.auth.currentTenant);
  
  const { data, isLoading, refetch } = useGetTenantsQuery(!isAuthenticated ? skipToken : undefined);

  const tenants = useMemo(() => data?.data || [], [data]);

  // Try to load the stored tenant on initial render
  useEffect(() => {
    const storedTenantId = localStorage.getItem('currentTenantId');

    if (storedTenantId && tenants.length > 0 && !currentTenant) {
      const tenant = tenants.find((t) => t.id === storedTenantId);
      if (tenant) {
        dispatch(setCurrentTenantAction(tenant));
      } else if (tenants.length > 0) {
        // If the stored tenant is not found, default to the first tenant
        dispatch(setCurrentTenantAction(tenants[0]));
      }
    } else if (tenants.length > 0 && !currentTenant) {
      // Default to the first tenant if none is stored
      dispatch(setCurrentTenantAction(tenants[0]));
    }
  }, [tenants, currentTenant, dispatch]);

  // Save the current tenant ID to localStorage whenever it changes
  useEffect(() => {
    if (currentTenant) {
      localStorage.setItem('currentTenantId', currentTenant.id);
    }
  }, [currentTenant]);

  const handleSetCurrentTenant = (tenant: Tenant) => {
    dispatch(setCurrentTenantAction(tenant));
    // Refresh the page when tenant is switched
    window.location.reload();
  };

  const refreshTenants = () => {
    refetch();
  };

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        tenants,
        isLoading,
        setCurrentTenant: handleSetCurrentTenant,
        refreshTenants,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
